/**
 * Numbering follows the boxes on the ICS 206-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS206CG.ts`.
 *
 * Boxes 1-3 are the read-only header; boxes 4-6 are the three tables, whose
 * columns are listed separately below; box 7 is the only free text on the card;
 * boxes 8 and 9 are the two signatures.
 *
 * The Coast Guard sheet numbers these differently from the NIMS 206 the docx
 * descriptor is read off, which runs the three tables as boxes 3-5 and the
 * signatures as 7 and 8. It also prints "Reviewed by" where the descriptor says
 * "Approved by". That is why the card labels its boxes from here rather than
 * from the descriptor; the stored signature role stays 'Approved By'.
 */
export const ICS206_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  operationalPeriod: '3. Operational Period (Date/Time):',
  aidStations: '4. Medical Aid Stations:',
  transportation: '5. Transportation:',
  hospitals: '6. Hospitals:',
  specialProcedures: '7. Special Medical Emergency Procedures:',
  preparedBy: '8. Prepared by:',
  reviewedBy: '9. Reviewed by (Safety Officer):',
} as const;

/** What each printed column of box 4 holds. */
export const ICS206_AID_STATION_COLUMNS = [
  { label: 'Name', source: 'name' },
  { label: 'Location', source: 'location' },
  { label: 'Contact Information', source: 'contact_number' },
  { label: 'Paramedics On site?', source: 'paramedics_on_site' },
] as const;

/**
 * What each printed column of box 5 holds.
 *
 * Level of Service prints as one ALS/BLS pair, which is why the two booleans
 * behind it are mutually exclusive on the card.
 */
export const ICS206_TRANSPORTATION_COLUMNS = [
  { label: 'Ambulance Service', source: 'ambulance_service' },
  { label: 'Location', source: 'address' },
  { label: 'Contact Information', source: 'contact_number' },
  { label: 'Level of Service', source: 'als and bls' },
] as const;

/** What each printed column of box 6 holds. */
export const ICS206_HOSPITAL_COLUMNS = [
  { label: 'Name', source: 'hospital_name' },
  { label: 'Address, Lat/Long if Helipad', source: 'address' },
  { label: 'Contact Information', source: 'contact_number' },
  { label: 'Travel Time Air', source: 'travel_time_air' },
  { label: 'Travel Time Ground', source: 'travel_time_ground' },
  { label: 'Trauma Center', source: 'trauma_center and trauma_center_level' },
  { label: 'Burn Center', source: 'burn_center' },
  { label: 'Helipad', source: 'heli_pad' },
] as const;

export const ICS206_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

/** Mirrors `resolveOperationalPeriodValues`, which the PDF export uses. */
export const ICS206_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

export const ICS206_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

/**
 * The three tables are incident-scoped rather than period-scoped, so the same
 * rows print on every period's 206. Deleting a row archives it.
 */
export const ICS206_AID_STATIONS_HINT =
  'Aid stations belong to the incident, not to one operational period, so the same rows print on every period. Deleting one archives it rather than erasing it.';

export const ICS206_TRANSPORTATION_HINT =
  'Ambulance services belong to the incident, not to one operational period. A row is either ALS or BLS, never both, because the printed form gives it one pair of boxes.';

export const ICS206_HOSPITALS_HINT =
  'Hospitals belong to the incident, not to one operational period. Give the lat/long in the address when the hospital has a helipad, as the printed form asks.';

export const ICS206_SPECIAL_PROCEDURES_HINT =
  'Shared with the incident information record, so the same text prints wherever else the incident reports special medical emergency procedures.';
